import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import type { Coordinate } from '../models/editor-feature';
import {
  isCorneredTool,
  isDrawingTool,
  type CorneredTool,
  type DrawTarget,
  type EditorTool,
} from '../models/editor-tool';
import {
  canCloseRing,
  ringToMultiPolygon,
  shapeBetween,
  squareCornerFrom,
  straightenFrom,
} from '../utils/drawing';
import { distanceBetween, pathLength, polygonArea } from '../utils/measurement';
import type { Projection } from '../utils/projection';
import { isSamePoint } from '../utils/snapping';

/**
 * The shape currently being drawn, before it becomes a feature.
 *
 * Held apart from the document on purpose. A half-drawn polygon is not a thing the plan
 * contains — it has no identity, it must not be selectable, and above all it must not enter
 * the undo stack, or every click of a ten-click outline would need its own undo and Esc
 * would leave a trail of fragments behind it.
 */

export type DraftKind = 'polygon' | CorneredTool;

export interface Draft {
  readonly kind: DraftKind;
  /** Points committed so far, in plan coordinates. */
  readonly points: readonly Coordinate[];
  /** Where the pointer is now, so the segment being aimed can be drawn. */
  readonly cursor?: Coordinate;
  /**
   * Where the pointer really is, before any straightening. Kept so that pressing or letting go
   * of the modifier can re-aim the segment at once, without waiting for the mouse to move.
   */
  readonly rawCursor?: Coordinate;
}

export interface DrawingState {
  readonly draft: Draft | undefined;
  /** What the draft measures so far, ready to print — a length, or an area. */
  readonly readout: string | undefined;
  /**
   * A click on the surface. Returns true when it was consumed by a drawing tool.
   *
   * `straighten` is whether Shift, Ctrl/⌘ or Alt is held, which keeps the segment from the last
   * point level or upright.
   */
  readonly onClick: (point: Coordinate, straighten?: boolean) => boolean;
  readonly onMove: (point: Coordinate, straighten?: boolean) => void;
  /** Closes the polygon being drawn. Silent when there is nothing to finish. */
  readonly onComplete: () => void;
  readonly onCancel: () => void;
  /** True while something is half-drawn, so Esc and the tool rail know there is state to drop. */
  readonly isDrawing: boolean;
}

interface Props {
  readonly tool: EditorTool;
  readonly target: DrawTarget;
  readonly projection: Projection;
  readonly formatLength: (units: number) => string;
  readonly formatArea: (squareUnits: number) => string;
  /** Called when a drawn shape is finished and should become a feature. */
  readonly onPolygonDrawn: (coordinates: ReturnType<typeof ringToMultiPolygon>, target: DrawTarget) => void;
  /**
   * Called when a line is finished. A line is not an area, so it is handed over as its two
   * ends and the caller decides how thick to sweep it. Without this the line tool does nothing.
   */
  readonly onLineDrawn?: (from: Coordinate, to: Coordinate) => void;
}

export const useDrawing = ({
  tool,
  target,
  projection,
  formatLength,
  formatArea,
  onPolygonDrawn,
  onLineDrawn,
}: Props): DrawingState => {
  const [draft, setDraft] = useState<Draft | undefined>();
  // Whether a straightening key is down, as the keyboard last reported it. The pointer event's
  // own flags are the better source and win whenever one arrives; this is what lets the preview
  // react to the key alone, with the mouse held still.
  const straightenKey = useRef(false);

  /** The point a segment from the last committed point should aim at. */
  const aimFrom = useCallback(
    (current: Draft | undefined, point: Coordinate, straighten: boolean): Coordinate => {
      // Only for the tools that draw segments from a point. A box shape is already square to
      // the axes, so there is nothing for the key to straighten.
      if (!straighten || !current || !current.points.length || (current.kind !== 'polygon' && current.kind !== 'line')) {
        return point;
      }

      return straightenFrom(current.points[current.points.length - 1], point);
    },
    [],
  );

  const onCancel = useCallback(() => setDraft(undefined), []);

  const finishPolygon = useCallback(
    (points: readonly Coordinate[]) => {
      if (!canCloseRing(points)) {
        // Two points enclose nothing. Dropped rather than stored, and the admin sees the
        // draft disappear rather than hunting for an invisible shape.
        setDraft(undefined);
        return;
      }

      onPolygonDrawn(ringToMultiPolygon(points), target);
      setDraft(undefined);
    },
    [onPolygonDrawn, target],
  );

  const onClick = useCallback(
    (rawPoint: Coordinate, straighten = straightenKey.current): boolean => {
      if (!isDrawingTool(tool)) {
        return false;
      }

      const point = aimFrom(draft, rawPoint, straighten);

      // --- a shape that takes two points: opposite corners, or a line's two ends
      if (isCorneredTool(tool)) {
        if (!draft) {
          setDraft({ kind: tool, points: [point], cursor: point });
          return true;
        }

        const [from] = draft.points;

        if (tool === 'line') {
          // Two clicks on the same spot are a double-click, not a line.
          if (distanceBetween(from, point) > 0) {
            onLineDrawn?.(from, point);
          }
        } else {
          // Null for a box with no width or no height, which encloses nothing whatever shape
          // is drawn inside it.
          const shape = shapeBetween(tool, from, point);

          if (shape) {
            onPolygonDrawn(shape, target);
          }
        }

        setDraft(undefined);
        return true;
      }

      // --- a shape that takes many points
      if (!draft) {
        setDraft({ kind: 'polygon', points: [point], cursor: point });
        return true;
      }

      // Clicking the first vertex again closes the shape, which is the gesture every drawing
      // tool uses and the one an admin will try before looking for a button.
      // Tested against where the pointer really is: straightened, a click right on the first
      // corner could be pulled away from it and leave the shape unclosable.
      if (draft.points.length >= 3 && (
        isSamePoint(draft.points[0], point, projection) || isSamePoint(draft.points[0], rawPoint, projection)
      )) {
        finishPolygon(draft.points);
        return true;
      }

      setDraft({ ...draft, points: [...draft.points, point], cursor: point });
      return true;
    },
    [
      tool,
      draft,
      aimFrom,
      projection,
      target,
      finishPolygon,
      onPolygonDrawn,
      onLineDrawn,
    ],
  );

  /**
   * Enter: finishes what is being drawn.
   *
   * A polygon closes on the corners placed so far. A line or a box shape ends where the pointer
   * is — the same as the second click would — rather than being thrown away, which is what
   * pressing Enter to finish used to do to them.
   */
  const onComplete = useCallback(() => {
    if (!draft) {
      return;
    }

    if (draft.kind === 'polygon') {
      finishPolygon(draft.points);
      return;
    }

    const end = draft.rawCursor ?? draft.cursor;

    if (end) {
      onClick(end);
      return;
    }

    setDraft(undefined);
  }, [draft, finishPolygon, onClick]);

  const onMove = useCallback(
    (point: Coordinate, straighten = straightenKey.current) => {
      straightenKey.current = straighten;
      setDraft((current) => (current
        ? { ...current, rawCursor: point, cursor: aimFrom(current, point, straighten) }
        : current));
    },
    [aimFrom],
  );

  const drawing = draft !== undefined;

  // Pressing or releasing the key re-aims the segment straight away. Listened for only while
  // something is being drawn, so the rest of the editor's keys are untouched.
  useEffect(() => {
    if (!drawing) {
      return undefined;
    }

    const update = (event: KeyboardEvent): void => {
      if (!['Shift', 'Control', 'Meta', 'Alt'].includes(event.key)) {
        return;
      }

      // Alt on its own would otherwise hand focus to the browser's menu bar on Windows.
      if (event.key === 'Alt') {
        event.preventDefault();
      }

      const straighten = event.shiftKey || event.ctrlKey || event.metaKey || event.altKey;
      straightenKey.current = straighten;

      setDraft((current) => (current?.rawCursor
        ? { ...current, cursor: aimFrom(current, current.rawCursor, straighten) }
        : current));
    };

    window.addEventListener('keydown', update);
    window.addEventListener('keyup', update);

    return () => {
      window.removeEventListener('keydown', update);
      window.removeEventListener('keyup', update);
    };
  }, [drawing, aimFrom]);

  /**
   * What the draft measures so far.
   *
   * Shown while drawing rather than only at the end: an edge being dragged to 3.2 m is worth
   * knowing before the click, not after it, and it is the only way to draw something to a
   * dimension rather than to the eye.
   */
  const readout = useMemo((): string | undefined => {
    if (!draft || !draft.cursor) {
      return undefined;
    }

    const points = [...draft.points, draft.cursor];
    const [from] = draft.points;

    if (draft.kind === 'line') {
      return formatLength(distanceBetween(from, draft.cursor));
    }

    // One figure for a shape whose sides are all the same: the side of a square, the diameter
    // of a circle.
    if (draft.kind === 'square' || draft.kind === 'circle') {
      return formatLength(Math.abs(squareCornerFrom(from, draft.cursor)[0] - from[0]));
    }

    if (draft.kind !== 'polygon') {
      // The box, for everything else drawn inside one. For an ellipse that box is its two
      // diameters, which is how a round table is specified and how a pillar is measured.
      return `${formatLength(Math.abs(draft.cursor[0] - from[0]))} × ${formatLength(Math.abs(draft.cursor[1] - from[1]))}`;
    }

    return points.length >= 3
      ? formatArea(polygonArea(ringToMultiPolygon(points)))
      : formatLength(pathLength(points));
  }, [draft, formatLength, formatArea]);

  return {
    draft,
    readout,
    onClick,
    onMove,
    onComplete,
    onCancel,
    isDrawing: drawing,
  };
};

export default useDrawing;
